import { useEffect, useRef, useState, type FormEvent } from 'react'
import type { User } from 'firebase/auth'
import { ArrowRight, KeyRound, UserCircle2, X } from 'lucide-react'

type Mode = 'signin' | 'create' | 'profile'

export function AuthModal({ user, intent, googleEnabled, onClose, onGoogle, onEmailSignIn, onEmailCreate, onReset, onProfile, errorText }: {
  user: User | null
  intent: string
  googleEnabled: boolean
  onClose: () => void
  onGoogle: () => Promise<void>
  onEmailSignIn: (email: string, password: string) => Promise<void>
  onEmailCreate: (email: string, password: string, displayName: string) => Promise<void>
  onReset: (email: string) => Promise<void>
  onProfile: (displayName: string, photoURL: string | null) => Promise<void>
  errorText: (error: unknown) => string
}) {
  const [mode, setMode] = useState<Mode>(user ? 'profile' : 'signin')
  const [email, setEmail] = useState(user?.email ?? '')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState(user?.displayName ?? '')
  const [photoURL, setPhotoURL] = useState(user?.photoURL ?? '')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'Tab' && panel.current) {
        const focusable = [...panel.current.querySelectorAll<HTMLElement>('button,input')].filter((element) => !element.hasAttribute('disabled'))
        if (!focusable.length) return
        const first = focusable[0]; const last = focusable.at(-1)!
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
      }
    }
    window.addEventListener('keydown', keydown)
    return () => window.removeEventListener('keydown', keydown)
  }, [onClose])

  const run = async (action: () => Promise<void>, success?: string) => {
    setBusy(true); setMessage('')
    try { await action(); if (success) setMessage(success) } catch (error) { setMessage(errorText(error)) } finally { setBusy(false) }
  }
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (mode === 'profile') void run(() => onProfile(displayName, photoURL || null))
    else if (mode === 'create') void run(() => onEmailCreate(email, password, displayName))
    else void run(() => onEmailSignIn(email, password))
  }
  const reason = intent === 'pulse' ? 'Sign in to Pulse this signal.' : intent === 'reply' ? 'Sign in to reply.' : intent === 'comment' ? 'Sign in to join the discussion.' : 'Your OMIKINA community profile.'

  return <div className="auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-title" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
    <div className="auth-panel" ref={panel}>
      <button className="auth-close" onClick={onClose} aria-label="Close"><X/></button>
      <span className="eyebrow">OMIKINA COMMUNITY</span>
      <UserCircle2 className="auth-mark" aria-hidden="true"/>
      <h2 id="auth-title">{mode === 'profile' ? 'Your public profile' : mode === 'create' ? 'Create an account' : 'Continue the signal'}</h2>
      <p>{reason} Your email stays private; only your display name and optional avatar appear with comments.</p>
      {mode !== 'profile' && googleEnabled && <button className="auth-google" disabled={busy} onClick={() => void run(onGoogle)}>CONTINUE WITH GOOGLE <ArrowRight/></button>}
      <form onSubmit={submit}>
        {(mode === 'create' || mode === 'profile') && <label>DISPLAY NAME<input autoFocus={mode === 'profile'} required minLength={2} maxLength={60} value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="nickname"/></label>}
        {mode === 'profile' && <label>AVATAR URL · OPTIONAL<input type="url" value={photoURL} onChange={(event) => setPhotoURL(event.target.value)} placeholder="https://…"/></label>}
        {mode !== 'profile' && <><label>EMAIL<input autoFocus type="email" required value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email"/></label><label>PASSWORD<input type="password" required minLength={6} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={mode === 'create' ? 'new-password' : 'current-password'}/></label></>}
        <button className="auth-submit" disabled={busy}>{busy ? 'WORKING…' : mode === 'profile' ? 'SAVE PROFILE' : mode === 'create' ? 'CREATE ACCOUNT' : 'SIGN IN'} <KeyRound/></button>
      </form>
      {message && <p className="auth-message" role="status">{message}</p>}
      {mode === 'signin' && <div className="auth-secondary"><button onClick={() => setMode('create')}>CREATE ACCOUNT</button><button disabled={!email || busy} onClick={() => void run(() => onReset(email), 'Password reset email sent.')}>RESET PASSWORD</button></div>}
      {mode === 'create' && <button className="auth-mode" onClick={() => setMode('signin')}>ALREADY HAVE AN ACCOUNT? SIGN IN</button>}
    </div>
  </div>
}
